import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  List,
  ListItem,
  Text,
} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'

import SignInLayout from '../../layouts/SignInLayout'
import {TickedIcon, SolidTickedIcon} from '../../icons'
import PasswordInput from '../../components/PasswordInput'
import Alert from '../../components/Alert'

import {PASSWORD_RULES, meetsRequiredRules} from './passwordRules'

const PasswordRule = ({isMet, label}) => (
  <ListItem display="flex" alignItems="center" gap="xs" mb="xs">
    {isMet ? (
      <SolidTickedIcon color="mld.success.700" aria-hidden />
    ) : (
      <TickedIcon color="mld.neutral.700" aria-hidden />
    )}
    <Text
      textStyle="body-small"
      color={isMet ? 'mld.neutral.0' : 'mld.neutral.500'}
    >
      {label}
    </Text>
  </ListItem>
)

PasswordRule.propTypes = {
  isMet: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
}

const CreatePasswordScreen = ({
  authenticityToken,
  createPasswordPath,
  errorMessages,
  passwordSet,
  resourceName,
  token,
}) => {
  const {t} = useTranslation('createPasswordPage')
  const [password, setPassword] = React.useState('')
  // The form posts and the browser navigates, so the button carries the wait.
  // Disabling it also stops a second submission racing the first, which would
  // spend the link twice and land the person on the "ask for a new one" screen.
  const [submitting, setSubmitting] = React.useState(false)

  // Replacing a password rather than choosing a first one only changes the
  // wording. The screen reads which it is from the server, not from the URL, so
  // nobody can dress one up as the other.
  const context = passwordSet ? 'reset' : 'create'

  return (
    <SignInLayout>
      <Box color="mld.neutral.0" width="100%">
        <Text textStyle="headline3" textAlign="center" py="md" mb="sm">
          {t('createPasswordPage:title', {context})}
        </Text>

        {errorMessages.length > 0 && (
          <Alert
            status="error"
            mb="md"
            message={
              <Flex direction="column">
                {errorMessages.map((message) => (
                  <Text textStyle="body-small" key={message}>
                    {message}
                  </Text>
                ))}
              </Flex>
            }
          />
        )}

        <form
          method="POST"
          acceptCharset="UTF-8"
          action={createPasswordPath}
          onSubmit={() => setSubmitting(true)}
        >
          <input type="hidden" name="_method" value="put" />
          <input
            type="hidden"
            name="authenticity_token"
            value={authenticityToken}
          />
          <input
            type="hidden"
            name={`${resourceName}[reset_password_token]`}
            value={token}
          />

          <FormControl pb="md">
            <FormLabel htmlFor="password" fontSize="base">
              {t('createPasswordPage:passwordLabel')}
            </FormLabel>
            <PasswordInput
              name={`${resourceName}[password]`}
              id="password"
              autoFocus
              autoComplete="new-password"
              onChange={(event) => setPassword(event.target.value)}
              value={password}
            />
          </FormControl>

          <Text textStyle="caption-medium" color="mld.neutral.0" mb="sm">
            {t('createPasswordPage:requirementsHeading')}
          </Text>
          <List mb="md">
            {PASSWORD_RULES.map((rule) => (
              <PasswordRule
                key={rule.key}
                isMet={rule.met(password)}
                label={t(`createPasswordPage:requirements.${rule.key}`)}
              />
            ))}
          </List>

          <Button
            variant="primary"
            colorScheme="primary"
            size="lg"
            width="100%"
            type="submit"
            isLoading={submitting}
            // Keeps the label beside the spinner: without it Chakra swaps the
            // text out and the button loses its accessible name mid-submit.
            loadingText={t('createPasswordPage:submit', {context})}
            isDisabled={!meetsRequiredRules(password) || submitting}
          >
            {t('createPasswordPage:submit', {context})}
          </Button>
        </form>
      </Box>
    </SignInLayout>
  )
}

CreatePasswordScreen.propTypes = {
  passwordSet: PropTypes.bool,
  resourceName: PropTypes.string,
  authenticityToken: PropTypes.string.isRequired,
  createPasswordPath: PropTypes.string.isRequired,
  errorMessages: PropTypes.arrayOf(PropTypes.string),
  token: PropTypes.string.isRequired,
}

CreatePasswordScreen.defaultProps = {
  errorMessages: [],
  passwordSet: false,
  resourceName: 'supplier_login',
}

export default CreatePasswordScreen
